/**
 * EL INFORME MENSUAL QUE RECIBE EL CLIENTE (artefacto del 27-10, `bloques.formato ===
 * "con_checkin_2710"`). Se abre en Seguimiento › Histórico de informes.
 *
 *     INFORME DE OCTUBRE · DEL ... AL ... · 18 REGISTROS
 *     TU INFORME          Objetivo · Ciclo · Check-in
 *     1 Feedback de tu entrenador        5 Tu día tipo (en veces, sin cantidades)
 *     2 Tu peso (+ Ver pesos registrados) 6 Tus alimentos (los 20 más repetidos)
 *     3 Cómo te has sentido               7 Tus fotos y tus medidas
 *     4 Lo que has hecho (% y tiras)      8 Lo que me has pedido
 *
 * Todo llega contado de `core/informe_con_checkin`: aquí no se calcula nada. Las piezas
 * son las del informe de antes (`InformeDelMes`) y las del quincenal (`piezas`), que dicen
 * lo mismo con las mismas filas. Los informes guardados con el formato anterior se siguen
 * leyendo con `InformeDelMes`: un informe es la foto de un momento.
 */
import React, { useState } from 'react';
import { num1 } from '../../lib/numeros';
import { plural } from '../../lib/labels';
import { ACTIVIDAD_VALOR } from '../../lib/respuestasDelCierre';
import { FeedbackDelInforme, Fila, Tarjeta, TusFotos } from './InformeDelMes';
import { FilasDePeso, FilasDeSensaciones } from './piezas';

const ORANGE = '#FF671F';
const COLOR = { verde: 'text-emerald-500', rojo: 'text-red-500', gris: 'text-foreground/30' };

/**
 * EL COLOR DE CADA DÍA DE LAS TIRAS. Lleno lo hecho, a medias lo que se hizo de otra
 * forma, y vacío lo que faltó. Lo que no tocaba (descanso, sin cardio ese día, sin
 * suplementos pautados) va con el borde, para que no se lea como un fallo.
 */
const TONO = {
    hecho: { fondo: ORANGE }, cuadrada: { fondo: ORANGE }, tal_cual: { fondo: ORANGE },
    master: { fondo: ORANGE }, creces: { fondo: ORANGE },
    adaptado: { fondo: `${ORANGE}8C` }, sin_cuadrar: { fondo: `${ORANGE}8C` }, minimo: { fondo: `${ORANGE}8C` },
    cerca: { fondo: `${ORANGE}4D` },
    no_entreno: { clase: 'bg-foreground/40' }, menos_5000: { clase: 'bg-foreground/40' },
    descanso: { clase: 'border border-border' }, no_toca: { clase: 'border border-border' },
};
const estiloDe = (tono) => TONO[tono] || { clase: 'bg-foreground/10' };

const Muestra = ({ tono }) => {
    const e = estiloDe(tono);
    return <span className={`inline-block w-2.5 h-2.5 rounded-sm ${e.clase || ''}`}
        style={e.fondo ? { backgroundColor: e.fondo } : undefined} />;
};

/** Un cuadrito por día, en orden. */
const Tira = ({ tira, testid }) => (
    <div className="flex gap-[3px]" data-testid={testid}>
        {(tira || []).map((tono, i) => {
            const e = estiloDe(tono);
            return <span key={i} className={`h-3 flex-1 min-w-0 rounded-[3px] ${e.clase || ''}`}
                style={e.fondo ? { backgroundColor: e.fondo } : undefined} />;
        })}
    </div>
);

/**
 * Los tramos de actividad con el nombre del check-in, sin la coletilla del paréntesis
 * («Menos de 5.000 (deja el móvil...)»): el servidor manda solo la clave.
 */
const tramo = (clave) => (ACTIVIDAD_VALOR[clave] || clave).split(' (')[0];

/** «Cuadrada · 11  Sin cuadrar · 5  Sin crear · 2». */
const Leyenda = ({ leyenda }) => (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
        {leyenda.map((l) => (
            <span key={l.tono} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Muestra tono={l.tono} />{l.etiqueta || tramo(l.tono)} · {l.dias}
            </span>
        ))}
    </div>
);

const Rotulo = ({ children, derecha }) => (
    <div className="flex items-baseline justify-between gap-3 pt-2 px-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{children}</p>
        {derecha && <p className="text-[11px] text-muted-foreground text-right">{derecha}</p>}
    </div>
);

/**
 * «VER PESOS REGISTRADOS»: todos los registros desde el inicio, en barras. Naranjas los
 * que quedaron como peso de su semana, grises los del día. Debajo, los diez últimos.
 */
const PesosRegistrados = ({ registros }) => {
    const lista = registros?.lista || [];
    if (!lista.length) return null;
    const enOrden = [...lista].reverse();
    const valores = enOrden.map((r) => Number(r.numero));
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    const alto = (v) => (max === min ? 60 : 20 + (80 * (v - min)) / (max - min));
    const primero = enOrden[0];
    const ultimo = enOrden[enOrden.length - 1];
    const resto = lista.length - 10;
    return (
        <div className="space-y-3 pt-3 border-t border-border" data-testid="informe-pesos-registrados">
            {registros.titulo && (
                <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
                    {registros.titulo}
                </p>
            )}
            <div className="flex items-end gap-[2px] h-24">
                {enOrden.map((r) => (
                    <span key={r.fecha} title={`${r.label} · ${r.valor} kg`}
                        className={`flex-1 min-w-0 rounded-t-[2px] ${r.semanal ? '' : 'bg-foreground/20'}`}
                        style={{ height: `${alto(Number(r.numero))}%`,
                                 ...(r.semanal ? { backgroundColor: ORANGE } : {}) }} />
                ))}
            </div>
            <div className="flex justify-between gap-3 text-[11px] text-muted-foreground tabular-nums">
                <span>{primero.label} · {primero.valor} kg</span>
                <span className="text-right">{ultimo.label} · {ultimo.valor} kg</span>
            </div>
            <div className="flex gap-3 text-[11px] text-muted-foreground">
                <span className="flex items-center gap-1.5"><Muestra tono="hecho" />Peso de la semana</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-foreground/20" />Registro del día</span>
            </div>
            <div className="-my-1">
                {lista.slice(0, 10).map((r, i) => (
                    <div key={r.fecha} className={`flex items-baseline justify-between gap-3 py-2 ${i ? 'border-t border-border' : ''}`}>
                        <span className="text-sm text-foreground/80">
                            {r.label}
                            {r.semanal && (
                                <span className="ml-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
                                    Semanal
                                </span>
                            )}
                        </span>
                        <span className="text-sm font-bold tabular-nums whitespace-nowrap">{r.valor} kg</span>
                    </div>
                ))}
            </div>
            {resto > 0 && (
                <p className="text-[13px] text-muted-foreground">
                    {plural(resto, 'registro más', 'registros más')}, hasta el {primero.label.split(' ').slice(1).join(' ')}
                </p>
            )}
        </div>
    );
};

const InformeConCheckin = ({ informe, api, token, fotosDelFeedback }) => {
    const b = informe?.bloques;
    const [verPesos, setVerPesos] = useState(false);
    if (!b) return null;
    const { cabecera, feedback, peso, sensaciones, hecho, dia_tipo: dia, alimentos, medidas, pedido } = b;
    const conAnterior = (medidas?.filas || []).some((f) => f.mes);

    return (
        <div className="space-y-3" data-testid="informe-con-checkin">
            {/* ── CABECERA ── */}
            <div className="space-y-1">
                <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
                    {cabecera?.titulo}{cabecera?.periodo ? ` · ${cabecera.periodo}` : ''}
                </p>
                <h2 className="text-2xl font-bold text-foreground uppercase"
                    style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.02em' }}>
                    Tu informe
                </h2>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[13px] text-muted-foreground">
                    {cabecera?.objetivo && <span>Objetivo · <b className="text-foreground/80">{cabecera.objetivo}</b></span>}
                    {cabecera?.ciclo && <span>Ciclo · <b className="text-foreground/80">{cabecera.ciclo}</b></span>}
                    {cabecera?.checkin && <span>Check-in · <b className="text-foreground/80">{cabecera.checkin}</b></span>}
                </div>
            </div>

            {/* ── 1 · FEEDBACK DE TU ENTRENADOR ── */}
            <FeedbackDelInforme feedback={feedback} api={api} fotosDelFeedback={fotosDelFeedback}
                titulo="Feedback de tu entrenador" />

            {/* ── 2 · TU PESO ── */}
            {(peso?.filas || []).length > 0 && (
                <Tarjeta titulo="Tu peso" testid="informe-peso">
                    <div className="-my-1">
                        <FilasDePeso filas={peso.filas} prefijo="informe" />
                    </div>
                    {(peso.registros?.lista || []).length > 0 && (
                        <button type="button" onClick={() => setVerPesos((v) => !v)}
                            data-testid="informe-ver-pesos"
                            className="text-sm font-semibold" style={{ color: ORANGE }}>
                            {verPesos ? 'Ocultar pesos registrados' : 'Ver pesos registrados ›'}
                        </button>
                    )}
                    {verPesos && <PesosRegistrados registros={peso.registros} />}
                </Tarjeta>
            )}

            {/* ── 3 · CÓMO TE HAS SENTIDO ── */}
            {sensaciones?.hay && (
                <Tarjeta titulo="Cómo te has sentido" testid="informe-sensaciones">
                    <p className="text-[13px] text-muted-foreground -mt-1">{sensaciones.pie}</p>
                    <div className="-my-1">
                        <FilasDeSensaciones filas={sensaciones.filas} prefijo="informe" />
                    </div>
                </Tarjeta>
            )}

            {/* ── 4 · LO QUE HAS HECHO ── */}
            {hecho?.hay && (
                <Tarjeta titulo="Lo que has hecho" testid="informe-hecho">
                    {hecho.porcentaje != null && (
                        <div className="text-center pb-1" data-testid="informe-hecho-porcentaje">
                            <p className="text-4xl font-bold tabular-nums text-foreground" style={{ fontFamily: 'Barlow Condensed' }}>
                                {hecho.porcentaje} %
                            </p>
                            <p className="text-xs text-muted-foreground">{hecho.pie}</p>
                        </div>
                    )}
                    <div className="-my-1">
                        {hecho.filas.map((f, i) => (
                            <div key={f.clave} data-testid={`informe-hecho-${f.clave}`}
                                className={`py-3 space-y-2 ${i ? 'border-t border-border' : ''}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className="min-w-0">
                                        <span className="block text-sm text-foreground/80">{f.etiqueta}</span>
                                        {f.pie && <span className="block text-[11px] text-muted-foreground">{f.pie}</span>}
                                    </span>
                                    <span className="text-sm font-bold text-foreground tabular-nums text-right">
                                        {f.tramo ? `${tramo(f.tramo)} · ${plural(f.dias_del_tramo, 'día')}` : f.valor}
                                    </span>
                                </div>
                                <Tira tira={f.tira} testid={`informe-tira-${f.clave}`} />
                                {(f.leyenda || []).length > 0 && <Leyenda leyenda={f.leyenda} />}
                                {f.nota && <p className="text-[13px] text-muted-foreground">{f.nota}</p>}
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}

            {/* ── 5 · TU DÍA TIPO ── */}
            {dia?.hay && (
                <Tarjeta titulo="Tu día tipo" testid="informe-dia-tipo">
                    <p className="text-[13px] text-muted-foreground -mt-1">
                        Tu combinación más repetida en cada comida, y las veces que la has hecho. Solo los alimentos, sin cantidades.
                    </p>
                    <div className="-my-1">
                        {dia.filas.map((f, i) => (
                            <div key={f.clave} data-testid={`informe-dia-${f.clave}`}
                                className={`py-3 space-y-0.5 ${i ? 'border-t border-border' : ''}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: ORANGE }}>
                                        {f.momento ? `${f.momento} · ` : ''}{f.nombre}
                                    </span>
                                    <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground text-right">
                                        {f.varia ? 'Sin combinación repetida' : plural(f.veces, 'vez', 'veces')}
                                    </span>
                                </div>
                                <p className="text-sm text-foreground">
                                    {f.varia ? f.texto : (f.items || []).map((x) => x.nombre).join(' · ')}
                                </p>
                            </div>
                        ))}
                    </div>
                </Tarjeta>
            )}

            {/* ── 6 · TUS ALIMENTOS ── */}
            {alimentos?.hay && (
                <Tarjeta titulo="Tus alimentos" testid="informe-alimentos">
                    <p className="text-[13px] text-muted-foreground -mt-1">
                        Tus {plural([...(alimentos.proteina || []), ...(alimentos.hidratos || []), ...(alimentos.grasas || [])].length,
                            'alimento más repetido', 'alimentos más repetidos')}, por categoría. El número son las veces que lo has puesto en una comida, no los gramos.
                    </p>
                    {[['proteina', 'Proteína'], ['hidratos', 'Hidratos'], ['grasas', 'Grasa']].map(([k, l]) => (
                        (alimentos[k] || []).length > 0 && (
                            <div key={k} data-testid={`informe-alimentos-${k}`}>
                                <p className="text-[13px] font-bold mb-1" style={{ color: ORANGE }}>
                                    {l} ({alimentos[k].length})
                                </p>
                                <div className="-my-1">
                                    {alimentos[k].map((a, i) => (
                                        <Fila key={a.nombre} primera={i === 0} que={a.nombre} valor={a.veces} />
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                </Tarjeta>
            )}

            {/* ── 7 · TUS FOTOS Y TUS MEDIDAS ── */}
            {api && <TusFotos api={api} token={token} tipo="mensual" />}
            {medidas?.hay && (
                <Tarjeta titulo="Tus medidas" testid="informe-medidas">
                    <div className={`grid gap-x-3 items-baseline ${conAnterior
                        ? 'grid-cols-[minmax(0,1fr)_auto_auto_auto]' : 'grid-cols-[minmax(0,1fr)_auto]'}`}>
                        <span />
                        <span className="text-[11px] text-muted-foreground text-right">Ahora</span>
                        {conAnterior && <span className="text-[11px] text-muted-foreground text-right">Mes ant.</span>}
                        {conAnterior && <span className="text-[11px] text-muted-foreground text-right">Cambio</span>}
                        {medidas.filas.map((f) => (
                            <React.Fragment key={f.clave}>
                                <span className="text-sm text-foreground/80 border-t border-border py-2 min-w-0">{f.etiqueta}</span>
                                <span className="text-sm font-bold tabular-nums text-right border-t border-border py-2"
                                    data-testid={`medida-ahora-${f.clave}`}>
                                    {num1(f.valor)}
                                </span>
                                {conAnterior && (
                                    <span className="text-sm tabular-nums text-right text-muted-foreground border-t border-border py-2">
                                        {f.mes?.antes != null ? num1(f.mes.antes) : '-'}
                                    </span>
                                )}
                                {conAnterior && (
                                    <span className={`text-sm font-bold tabular-nums text-right border-t border-border py-2 ${
                                        f.mes ? COLOR[f.mes.color] : 'text-foreground/20'}`}>
                                        {/* Igual, «=» (artefacto): un «0» se lee como un dato que falta. */}
                                        {f.mes ? (f.mes.dif === 0 ? '=' : f.mes.label) : '-'}
                                    </span>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">En centímetros.</p>
                </Tarjeta>
            )}

            {/* ── 8 · LO QUE ME HAS PEDIDO ── */}
            {pedido?.hay && (
                <>
                    <Rotulo>Lo que me has pedido para el mes que viene</Rotulo>
                    <Tarjeta testid="informe-pedido">
                        <div className="-my-1">
                            {pedido.filas.map((f, i) => (
                                <div key={f.clave} data-testid={`informe-pedido-${f.clave}`}
                                    className={`py-2.5 ${i ? 'border-t border-border' : ''}`}>
                                    <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{f.etiqueta}</p>
                                    <p className="text-sm text-foreground mt-0.5 break-words">{f.texto}</p>
                                </div>
                            ))}
                        </div>
                    </Tarjeta>
                </>
            )}
        </div>
    );
};

export default InformeConCheckin;
